import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
const BUYERS = [{
  name: 'Lucas M.',
  location: 'Córdoba'
}, {
  name: 'Sofía R.',
  location: 'Rosario'
}, {
  name: 'Martín P.',
  location: 'Buenos Aires'
}, {
  name: 'Camila G.',
  location: 'La Plata'
}, {
  name: 'Federico A.',
  location: 'Mendoza'
}, {
  name: 'Valentina T.',
  location: 'Mar del Plata'
}, {
  name: 'Mateo S.',
  location: 'San Miguel de Tucumán'
}, {
  name: 'Agustina L.',
  location: 'Salta'
}, {
  name: 'Diego F.',
  location: 'Neuquén'
}, {
  name: 'Florencia B.',
  location: 'Bahía Blanca'
}, {
  name: 'Joaquín C.',
  location: 'Santa Fe'
}, {
  name: 'Micaela H.',
  location: 'San Juan'
}, {
  name: 'Tomás N.',
  location: 'Bariloche'
}, {
  name: 'Lucía V.',
  location: 'Resistencia'
}, {
  name: 'Nicolás O.',
  location: 'Posadas'
}, {
  name: 'Rocío E.',
  location: 'Corrientes'
}, {
  name: 'Bruno D.',
  location: 'Paraná'
}, {
  name: 'Antonella K.',
  location: 'San Luis'
}, {
  name: 'Franco I.',
  location: 'San Salvador de Jujuy'
}, {
  name: 'Julieta U.',
  location: 'Ushuaia'
}, {
  name: 'Gonzalo M.',
  location: 'Río Cuarto'
}, {
  name: 'Brenda S.',
  location: 'Comodoro Rivadavia'
}, {
  name: 'Ezequiel V.',
  location: 'Villa Carlos Paz'
}, {
  name: 'Carolina F.',
  location: 'Tandil'
}];
const PLANS = [{
  name: 'Plan Completo',
  price: '$19.990'
}, {
  name: 'Kit Pro',
  price: '$24.990'
}];
const TIME_AGO_OPTIONS = ['hace unos segundos', 'hace 1 minuto', 'hace 2 minutos', 'hace 3 minutos', 'hace 5 minutos', 'hace 8 minutos'];
interface Notification {
  id: number;
  buyer: (typeof BUYERS)[number];
  plan: (typeof PLANS)[number];
  timeAgo: string;
}
const randomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const randomInRange = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
export function PurchaseNotification() {
  const [notification, setNotification] = useState<Notification | null>(null);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let hideTimeoutId: ReturnType<typeof setTimeout>;
    let counter = 0;
    const generateNotification = (): Notification => ({
      id: ++counter,
      buyer: randomItem(BUYERS),
      plan: randomItem(PLANS),
      timeAgo: randomItem(TIME_AGO_OPTIONS)
    });
    const showNotification = () => {
      setNotification(generateNotification());
      // hide after 6 seconds visible
      hideTimeoutId = setTimeout(() => {
        setNotification(null);
        // schedule next between 40 and 60 seconds after hiding
        timeoutId = setTimeout(showNotification, randomInRange(40000, 60000));
      }, 6000);
    };
    // first notification after 12 seconds (so it doesn't feel intrusive on load)
    timeoutId = setTimeout(showNotification, 12000);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(hideTimeoutId);
    };
  }, []);
  const handleDismiss = () => {
    setNotification(null);
  };
  return <div className="fixed bottom-4 left-4 z-50 pointer-events-none sm:max-w-sm w-[calc(100%-2rem)] sm:w-auto" data-id="element-255">
      <AnimatePresence data-id="element-256">
        {notification && <motion.div key={notification.id} initial={{
        opacity: 0,
        x: -40,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -20,
        transition: {
          duration: 0.25
        }
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 24
      }} className="pointer-events-auto bg-surface/95 backdrop-blur-md border border-surface-light rounded-2xl p-3 sm:p-4 shadow-2xl flex items-start gap-3 relative overflow-hidden" data-id="element-257">
            {/* Accent strip */}
            <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary-dark" data-id="element-258" />

            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center mt-0.5" data-id="element-259">
              <CheckCircle2Icon className="w-5 h-5 text-primary" data-id="element-260" />
            </div>

            <div className="flex-1 min-w-0 pr-4" data-id="element-261">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-262">
                <span className="text-[10px] font-bold tracking-wider uppercase text-primary" data-id="element-263">
                  Compra verificada
                </span>
                <span className="w-1 h-1 rounded-full bg-primary animate-pulse" data-id="element-264" />
              </div>
              <p className="text-sm text-white leading-snug" data-id="element-265">
                <strong className="font-semibold" data-id="element-266">
                  {notification.buyer.name}
                </strong>{' '}
                de{' '}
                <span className="text-text-muted" data-id="element-267">
                  {notification.buyer.location}
                </span>{' '}
                compró el{' '}
                <strong className="text-primary-light" data-id="element-268">
                  {notification.plan.name}
                </strong>
              </p>
              <p className="text-[11px] text-text-muted mt-1" data-id="element-269">
                {notification.plan.price} ARS · {notification.timeAgo}
              </p>
            </div>

            <button onClick={handleDismiss} aria-label="Cerrar notificación" className="absolute top-2 right-2 text-text-muted/60 hover:text-white transition-colors p-1" data-id="element-270">
              <XIcon className="w-3.5 h-3.5" data-id="element-271" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}